/**
 * 장례식장 예상 비용 범위 계산.
 *
 * parlor.goi의 DirectionsCostGauge에 있던 구간 계산을 platform으로 옮긴 것이다.
 * 두 곳이 같은 숫자를 보여줘야 해서 구간 식을 그대로 유지한다.
 *
 * 입력은 parlors_detail.json의 expected_charge_sum(원 단위),
 * 출력은 화면 라벨과 같은 만원 단위다.
 */

/** 전국 무빈소 최저 38만원. 게이지 좌측 끝 */
export const NATIONAL_MIN_MANWON = 38;
/** 전국 일반장 최고 1,205만원. 게이지 우측 끝 */
export const NATIONAL_MAX_MANWON = 1205;

export interface ParlorExpectedCharge {
  무빈소: number;
  가족장: number;
  일반장: number;
}

export interface CostRange {
  /** 만원 단위 */
  min: number;
  /** 만원 단위 */
  max: number;
}

/**
 * 조문객 선택지에서 인원수를 뽑는다.
 *
 * 실험 159는 A안이 채택돼 앞으로는 기본 문구만 들어온다.
 *   기본  : "0명(무빈소)" / "50명 내외" / "200명 이상" / "모르겠어요"
 *   변형B : "조문 없이 가족끼리만 (빈소 없이)" / "가까운 사람들과 조용히 (50명 내외)" / "아직 예상하기 어려워요"
 * 실험 기간에 제출된 견적이 쿠키나 기존 요청에 남아 있을 수 있어 변형B 처리는 남겨둔다.
 * 변형B의 무빈소 문구에는 숫자가 없어서 키워드를 먼저 본다.
 *
 * @param condolers - quotation_request 쿠키의 condolers 또는 API의 request.condolers
 * @returns 예상 인원. 판단 불가면 null
 */
export function parseCondolers(condolers: string | undefined | null): number | null {
  if (typeof condolers !== "string" || !condolers) return null;
  if (condolers.includes("무빈소") || condolers.includes("빈소 없이")) return 0;

  const matched = condolers.match(/(\d+)/);
  return matched ? Number(matched[1]) : null;
}

/**
 * 장례식장의 예상 비용 범위를 만원 단위로 돌려준다.
 *
 * 조문객 수로 구간을 좁히지 않고 무빈소부터 일반장까지 전 구간을 쓴다.
 * 견적 폼(ParlorRequestQuestionStepByStep)의 게이지가 잡는 범위와 같은 기준이라
 * 같은 장례식장에 대해 두 화면이 다른 숫자를 보여주지 않는다.
 *
 *   MIN_PRICE = expected_charge_sum["무빈소"] / 10000
 *   MAX_PRICE = expected_charge_sum["일반장"] / 10000
 *
 * 조문객 기반으로 구간을 좁히면 숫자가 구체적이 되는 대신 실제 견적과 어긋날 여지가 커진다.
 * 비용은 보수적으로 알리는 편이 낫다는 판단으로 넓은 범위를 유지한다.
 *
 * @param charge - parlors_detail.json의 expected_charge_sum (원 단위)
 * @returns 만원 단위 범위. charge가 없으면 null
 */
export function getParlorCostRange(
  charge: ParlorExpectedCharge | undefined | null,
): CostRange | null {
  if (!charge) return null;

  const { 무빈소: mubinso, 일반장: general } = charge;
  if (![mubinso, general].every((v) => typeof v === "number" && Number.isFinite(v))) {
    return null;
  }

  return { min: Math.floor(mubinso / 10000), max: Math.floor(general / 10000) };
}
